import axios from "axios"
import { IS_WEB } from "@common/utils/mobileUtils"

/**
 * Captures the Meta click signals (`fbc`, `fbp`) once per browser session and
 * puts them on every outgoing API call, so the server-side conversions can
 * report them long after the browser is gone.
 *
 * WHY THIS RUNS AT IMPORT TIME, NOT IN A HOOK
 * The query string carrying `fbclid` is destroyed twice before any funnel
 * screen renders: the web bootstrap in App.js rewrites the address bar to "/"
 * for deep links, and FastTrackWelcomeScreen `navigation.replace`s into the
 * summer/brevet funnel with only the submissionGUID. Reading the URL from a
 * screen — or from an effect whose order we would have to guarantee — loses the
 * click id on most journeys. A module side effect runs before React mounts at
 * all, which is the only point where the original URL is still intact.
 */

/** Header names read back by App\Services\AdConversions\AdClickCapture. */
const AD_CLICK_HEADERS = {
    fbc: "X-Ad-Fbc",
    fbp: "X-Ad-Fbp",
}

const STORAGE_KEY = "masteur.adClick"

/**
 * Meta's `subdomainIndex` counts the levels of the domain the COOKIE IS DEFINED
 * ON — not those of the page's address: "com = 0, example.com = 1,
 * www.example.com = 2".
 *
 * The Pixel writes `_fbc` on the registrable root domain, so for us that is
 * masteur.com — one level — even though the funnels are served from
 * app.masteur.com. Hence 1, and it stays 1 whichever subdomain serves them.
 *
 * This is also the value the Pixel itself will write once it is wired up, which
 * is what keeps the two consistent. Counting the levels of the page address
 * instead would give 2 and produce a click id Meta never lines up with its own
 * cookie — no error, no warning, just a value that matches nothing.
 */
const FBC_SUBDOMAIN_INDEX = 1

/**
 * Build the `_fbc` value Meta expects from a raw fbclid, using the moment we
 * first saw it as the creation time.
 * Format: fb.<subdomainIndex>.<creationTimeMs>.<fbclid>
 */
export const buildFbc = (fbclid, nowMs) => {
    if (!fbclid) return null
    return `fb.${FBC_SUBDOMAIN_INDEX}.${nowMs}.${fbclid}`
}

/** Read one cookie by name. Returns null on anything unexpected. */
const readCookie = (cookieString, name) => {
    if (!cookieString) return null
    const match = cookieString.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`))
    return match ? decodeURIComponent(match[1]) : null
}

/**
 * The click signals available right now, cookie first.
 *
 * The cookie takes priority on purpose: the day the Meta Pixel is actually
 * loaded on our domain it will set a real `_fbc`, and that value is
 * authoritative over anything we reconstruct. Until then the fallback on the
 * URL's fbclid is the only source — and `_fbp` stays null, because only the
 * Pixel can set it.
 *
 * Pure and exported so the format can be tested without a browser.
 */
export const readAdClickSignals = ({ search, cookie, nowMs }) => {
    let fbc = readCookie(cookie, "_fbc")

    if (!fbc && search) {
        fbc = buildFbc(new URLSearchParams(search).get("fbclid"), nowMs)
    }

    return { fbc, fbp: readCookie(cookie, "_fbp") }
}

/**
 * Durée de vie de la trace, alignée sur la fenêtre d'attribution au clic de
 * Meta (7 jours). Passé ce délai, le clic ne peut plus rien attribuer chez
 * Meta : le garder ne sert qu'à le recoller au MAUVAIS prospect (voir
 * `captureAdClick`). Le vrai cookie `_fbc` que ce stockage imite expire lui
 * aussi — sans échéance, on serait plus fuyant que ce qu'on imite.
 */
const STORAGE_TTL_MS = 7 * 24 * 60 * 60 * 1000

/**
 * Local storage rather than session storage, because the click and the
 * conversion routinely happen in different tabs: an ad opens its own tab, and a
 * prospect who comes back from their history or from an e-mail opens another
 * one. Session storage is scoped to a single tab and would drop the click id in
 * exactly those journeys — the paid ones we are trying to measure.
 *
 * Serving a stale click id is the usual objection to a durable store, and it is
 * answered twice: the server refuses to overwrite a field it already holds, and
 * an entry older than {@link STORAGE_TTL_MS} is read as absent here.
 *
 * Best-effort either way: private browsing and blocked site data throw on access
 * rather than returning null, so every read and write is guarded. Losing the
 * cache only costs us the signal on a later page load.
 */
const readStored = (nowMs) => {
    try {
        const raw = window.localStorage?.getItem(STORAGE_KEY)
        const stored = raw ? JSON.parse(raw) : null
        if (!stored) return null

        // Une entrée sans date est antérieure à la péremption et ne peut pas
        // être vieillie : on la traite comme périmée plutôt que comme éternelle.
        const age = nowMs - stored.storedAtMs
        return age >= 0 && age < STORAGE_TTL_MS ? stored : null
    } catch (e) {
        return null
    }
}

const writeStored = (signals, nowMs) => {
    try {
        window.localStorage?.setItem(
            STORAGE_KEY,
            JSON.stringify({ ...signals, storedAtMs: nowMs })
        )
    } catch (e) {
        // Nothing to do — the headers below are still set for this page load.
    }
}

const applyHeaders = ({ fbc, fbp }) => {
    if (fbc) axios.defaults.headers.common[AD_CLICK_HEADERS.fbc] = fbc
    if (fbp) axios.defaults.headers.common[AD_CLICK_HEADERS.fbp] = fbp
}

/**
 * L'identifiant de clic contenu dans un `fb.<index>.<horodatage>.<clicId>`.
 * C'est la seule partie qui dit DE QUEL clic il s'agit — l'horodatage, lui, est
 * réécrit à chaque relecture d'une même URL.
 */
const clickIdOf = (fbc) => (fbc ? fbc.split(".").slice(3).join(".") : null)

/**
 * Capture once and start sending.
 *
 * La trace gardée l'emporte sur une relecture : le clic qui a ouvert le funnel
 * est celui qu'on rapporte, pas une pub croisée trois écrans plus loin. Écraser
 * le premier clic d'un MÊME prospect reste impossible de toute façon — le
 * serveur refuse d'y toucher, champ par champ. La même URL relue au
 * rechargement ne compte donc pas comme un nouveau clic : seul l'identifiant
 * est comparé, et l'horodatage d'origine est conservé.
 *
 * ── LE STOCKAGE APPARTIENT AU NAVIGATEUR, PAS AU PROSPECT ────────────────
 * Sur un poste partagé — « un commercial enchaîne deux familles », le cas que
 * le funnel anticipe déjà côté écran — la trace de la première famille se
 * recolle à la seconde. C'est le seul endroit où l'on enverrait à Meta une
 * attribution FAUSSE plutôt qu'absente, et un mensonge coûte plus cher qu'un
 * trou. Deux garde-fous, parce qu'il faut les deux :
 *
 *   1. un clic RÉELLEMENT nouveau (identifiant différent) l'emporte sur la
 *      trace gardée — il couvre la seconde famille qui arrive elle aussi par
 *      une pub ;
 *   2. la trace PÉRIME ({@link STORAGE_TTL_MS}) — seul garde-fou pour la
 *      seconde famille qui arrive SANS pub, où il n'y a rien à comparer.
 *
 * Le second borne le dégât, il ne l'annule pas : dans la fenêtre, une famille
 * venue du référencement naturel hérite encore du clic de la précédente. C'est
 * exactement ce que ferait le cookie `_fbc` du Pixel, qu'on ne peut pas faire
 * mieux que reproduire — et c'est borné, là où « pour toujours » ne l'était
 * pas.
 */
const captureAdClick = () => {
    if (!IS_WEB || typeof window === "undefined") return

    const nowMs = Date.now()
    const stored = readStored(nowMs)
    const fresh = readAdClickSignals({
        search: window.location?.search,
        cookie: typeof document === "undefined" ? null : document.cookie,
        nowMs,
    })

    const isNewClick =
        !!fresh.fbc && clickIdOf(fresh.fbc) !== clickIdOf(stored?.fbc)

    const signals = {
        fbc: (isNewClick ? fresh.fbc : stored?.fbc ?? fresh.fbc) ?? null,
        // `fbp` désigne le navigateur, pas le clic : le cookie du Pixel fait foi
        // quand il est là, la trace gardée sert de repli s'il a disparu.
        fbp: fresh.fbp ?? stored?.fbp ?? null,
    }

    if (!signals.fbc && !signals.fbp) return

    if (isNewClick || !stored) {
        // Un clic nouveau se date lui-même : c'est lui que l'échéance compte.
        writeStored(signals, nowMs)
    } else if (
        signals.fbc !== (stored.fbc ?? null) ||
        signals.fbp !== (stored.fbp ?? null)
    ) {
        // Un signal qui s'AJOUTE — typiquement le `fbp` que le Pixel finira par
        // poser (P-3313) — doit être conservé, sinon la trace ne sert de repli
        // que pour ce chargement-ci. Mais il ne REDATE pas la trace : l'échéance
        // appartient au clic, pas à la visite, et la rafraîchir à chaque page vue
        // rendrait la péremption inatteignable pour un prospect qui revient.
        writeStored(signals, stored.storedAtMs)
    }

    applyHeaders(signals)
}

captureAdClick()
